import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Banknote, Landmark, NotebookPen } from 'lucide-react'

import type {
    DeliverySettlement,
    SettlementMethod,
    SettlementOrder,
    TransferTarget,
} from '@/lib/delivery-settlement'
import type { DeliverySettlementFormValues } from '@/lib/schemas/delivery-settlement-schemas'
import type { SettlementEntryPoint } from '@/hooks/use-delivery-settlement-tracking'
import { useDeliverySettlementTracking } from '@/hooks/use-delivery-settlement-tracking'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyPreview } from '@/components/ui/money-preview'
import {
    isShippingFeeCollectible,
    toDeliverySettlement,
} from '@/lib/delivery-settlement'
import { formatVnd } from '@/lib/format'
import { orderRemaining } from '@/lib/order-money'
import { m } from '@/lib/paraglide/messages'
import {
    createDeliverySettlementSchema,
    emptyDeliverySettlement,
    holdsMoney,
} from '@/lib/schemas/delivery-settlement-schemas'

interface DeliverySettlementDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Order being delivered; drives the amount due and the fee checkbox. */
    order?: SettlementOrder
    /** Confirm-delivery request in flight — the only reason to disable submit. */
    isPending?: boolean
    /**
     * Return the confirm-delivery request so its result closes the analytics
     * attempt; a rejection counts as an API failure and is not rethrown.
     */
    onConfirm: (settlement: DeliverySettlement) => void | Promise<unknown>
    /** Which screen opened the popup (analytics dimension). */
    entryPoint: SettlementEntryPoint
}

type SettlementTracking = ReturnType<typeof useDeliverySettlementTracking>

const METHODS: {
    value: SettlementMethod
    label: () => string
    icon: typeof Banknote
}[] = [
    { value: 'cash', label: m.order_settlement_cash, icon: Banknote },
    { value: 'bank_transfer', label: m.order_settlement_bank, icon: Landmark },
    { value: 'credit', label: m.order_settlement_credit, icon: NotebookPen },
]

/** Where the customer's transfer landed (M-133); no default either. */
const TRANSFER_TARGETS: { value: TransferTarget; label: () => string }[] = [
    { value: 'htx', label: m.order_settlement_bank_htx },
    { value: 'driver', label: m.order_settlement_bank_driver },
]

/**
 * "Xác nhận giao hàng": the driver states how the customer paid. Shared by the
 * order detail, the driver's next-stop card and the trip order list so every
 * confirm path sends the same settlement payload. Nothing is preselected.
 */
export function DeliverySettlementDialog({
    open,
    onOpenChange,
    entryPoint,
    ...formProps
}: DeliverySettlementDialogProps) {
    const tracking = useDeliverySettlementTracking({
        open,
        entryPoint,
        feeCollectible: isShippingFeeCollectible(formProps.order),
    })
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.order_settlement_title()}</DialogTitle>
                    <DialogDescription>
                        {m.order_settlement_desc()}
                    </DialogDescription>
                </DialogHeader>
                {/* Mounted only while open, so every opening starts blank. */}
                <SettlementForm
                    {...formProps}
                    tracking={tracking}
                    onCancel={() => onOpenChange(false)}
                />
            </DialogContent>
        </Dialog>
    )
}

function SettlementForm({
    order,
    isPending = false,
    onConfirm,
    onCancel,
    tracking,
}: Omit<
    DeliverySettlementDialogProps,
    'open' | 'onOpenChange' | 'entryPoint'
> & {
    onCancel: () => void
    tracking: SettlementTracking
}) {
    const due = order ? Math.round(orderRemaining(order)) : 0
    const form = useForm<DeliverySettlementFormValues>({
        resolver: zodResolver(createDeliverySettlementSchema()),
        defaultValues: emptyDeliverySettlement(due),
    })
    const { control, handleSubmit, register } = form
    const method = useWatch({ control, name: 'method' })
    const transferTarget = useWatch({ control, name: 'transfer_target' })
    const cashAmount = useWatch({ control, name: 'cash_amount' })
    const feeCollectible = isShippingFeeCollectible(order)
    const showAmount = holdsMoney({
        method,
        transfer_target: transferTarget,
    })

    const onSubmit = handleSubmit((values) => {
        const settlement = toDeliverySettlement(
            { ...values, method: values.method as SettlementMethod },
            feeCollectible,
            due,
        )
        tracking.confirm(settlement, due, () => onConfirm(settlement))
    }, tracking.invalid)

    return (
        <FormProvider {...form}>
            <form noValidate onSubmit={onSubmit} className="space-y-3">
                {due > 0 && (
                    <p className="text-base font-semibold">
                        {m.order_settlement_amount_due({
                            amount: formatVnd(due),
                        })}
                    </p>
                )}
                <Controller
                    control={control}
                    name="method"
                    render={({ field }) => (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {METHODS.map(({ value, label, icon: Icon }) => (
                                <Button
                                    key={value}
                                    type="button"
                                    size="lg"
                                    aria-pressed={field.value === value}
                                    variant={
                                        field.value === value
                                            ? 'default'
                                            : 'outline'
                                    }
                                    onClick={() => {
                                        field.onChange(value)
                                        tracking.choose(value)
                                    }}
                                >
                                    <Icon className="size-5" />
                                    {label()}
                                </Button>
                            ))}
                        </div>
                    )}
                />
                <FieldError
                    name="method"
                    className="text-sm text-destructive"
                />
                {method === 'bank_transfer' && (
                    <fieldset className="space-y-2 rounded-md border bg-muted/40 p-3">
                        <legend className="px-1 text-sm font-medium">
                            {m.order_settlement_bank_target_label()}
                        </legend>
                        <Controller
                            control={control}
                            name="transfer_target"
                            render={({ field }) => (
                                <div className="grid grid-cols-2 gap-2">
                                    {TRANSFER_TARGETS.map(
                                        ({ value, label }) => (
                                            <Button
                                                key={value}
                                                type="button"
                                                className="h-auto min-h-11 whitespace-normal"
                                                aria-pressed={
                                                    field.value === value
                                                }
                                                variant={
                                                    field.value === value
                                                        ? 'default'
                                                        : 'outline'
                                                }
                                                onClick={() => {
                                                    field.onChange(value)
                                                    tracking.interact()
                                                }}
                                            >
                                                {label()}
                                            </Button>
                                        ),
                                    )}
                                </div>
                            )}
                        />
                        <FieldError
                            name="transfer_target"
                            className="text-sm text-destructive"
                        />
                        <p className="text-xs text-muted-foreground">
                            {transferTarget === 'driver'
                                ? m.order_settlement_bank_driver_hint()
                                : m.order_settlement_bank_hint()}
                        </p>
                    </fieldset>
                )}
                {showAmount && (
                    <Field
                        name="cash_amount"
                        label={
                            method === 'cash'
                                ? m.order_settlement_cash_amount()
                                : m.order_settlement_transfer_amount()
                        }
                    >
                        <Input
                            id="cash_amount"
                            inputMode="numeric"
                            aria-describedby="cash_amount_preview"
                            placeholder={m.order_settlement_cash_hint()}
                            {...register('cash_amount', {
                                onChange: tracking.interact,
                            })}
                        />
                        <MoneyPreview
                            id="cash_amount_preview"
                            value={cashAmount}
                        />
                    </Field>
                )}
                {feeCollectible && (
                    <div className="flex items-center gap-2 rounded-md border p-2">
                        <input
                            id="settlement-collect-fee"
                            type="checkbox"
                            className="h-5 w-5"
                            {...register('collect_fee', {
                                onChange: tracking.interact,
                            })}
                        />
                        <Label htmlFor="settlement-collect-fee">
                            {m.order_settlement_collect_fee({
                                amount: formatVnd(order?.shipping_fee ?? 0),
                            })}
                        </Label>
                    </div>
                )}
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        {m.common_close()}
                    </Button>
                    <Button type="submit" size="lg" disabled={isPending}>
                        {m.common_confirm()}
                    </Button>
                </DialogFooter>
            </form>
        </FormProvider>
    )
}
